<!--
  HlbServiceTransferStatsView.vue — 合利宝转接统计
  按 create_date 汇总合利宝客服转接通知记录数，不做去重。
  接口：GET /v1/analytics/order/stats/daily/hlb-service-transfer
-->
<template>
  <div class="hlb-transfer-stat-page">
    <a-card :bordered="false" class="filter-card">
      <a-form class="filter-form" layout="inline" :model="searchForm">
        <a-form-item label="开始日期">
          <a-date-picker
            v-model:value="searchForm.startDate"
            class="date-picker"
            value-format="YYYY-MM-DD"
            :allow-clear="false"
            @change="clearQuickDate"
          />
        </a-form-item>
        <a-form-item label="结束日期">
          <a-date-picker
            v-model:value="searchForm.endDate"
            class="date-picker"
            value-format="YYYY-MM-DD"
            :allow-clear="false"
            @change="clearQuickDate"
          />
        </a-form-item>
        <a-form-item label="快捷日期">
          <a-radio-group
            v-model:value="quickDateValue"
            class="quick-date-group"
            option-type="button"
            button-style="solid"
            @change="handleQuickDateChange"
          >
            <a-radio-button
              v-for="item in quickDateOptions"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" :loading="loading" @click="handleSearch">
              查询
            </a-button>
            <a-button :disabled="loading" @click="handleReset">重置</a-button>
          </a-space>
        </a-form-item>
      </a-form>
      <a-alert
        class="metric-rule"
        type="info"
        show-icon
        message="统计口径"
        description="直接按合利宝客服转接通知记录的 create_date 分组，每条记录计为一次，不按手机号、订单号或其他字段去重。"
      />
    </a-card>

    <a-card :bordered="false" class="chart-card" title="合利宝转接日趋势">
      <template #extra>
        <span class="range-label">{{ rangeLabel }}</span>
      </template>
      <a-spin :spinning="loading">
        <a-result
          v-if="loadError"
          status="warning"
          title="统计数据加载失败"
          :sub-title="loadError"
        >
          <template #extra>
            <a-button type="primary" @click="fetchData">重新加载</a-button>
          </template>
        </a-result>
        <template v-else-if="chartPoints.length">
          <v-chart
            class="trend-chart"
            :option="chartOption"
            autoresize
            role="img"
            :aria-label="chartAriaLabel"
          />
          <div class="chart-summary" aria-live="polite">
            <div class="summary-column">
              <div class="summary-title">最近有数据日期（{{ latestPoint.statDate }}）</div>
              <div class="summary-metrics">
                <span>
                  转接次数：<b class="transfer-value">{{ formatCount(latestPoint.transferCount) }}</b> 次
                </span>
              </div>
            </div>
            <div class="summary-column">
              <div class="summary-title">筛选范围汇总</div>
              <div class="summary-metrics">
                <span>
                  转接总次数：<b class="transfer-value">{{ formatCount(summary.transferCount) }}</b> 次
                </span>
              </div>
            </div>
          </div>
        </template>
        <a-empty v-else description="当前日期范围暂无合利宝转接数据" class="chart-empty" />
      </a-spin>
    </a-card>

    <a-card :bordered="false" title="合利宝转接统计明细">
      <a-table
        :columns="columns"
        :data-source="tableRows"
        :loading="loading"
        :pagination="pagination"
        row-key="statDate"
        :scroll="{ x: 360 }"
        size="small"
        bordered
        @change="handleTableChange"
      />
    </a-card>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { message } from "ant-design-vue";
import VChart from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import {
  AriaComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from "echarts/components";
import { analyticsUrl } from "@/utils/api";
import { get } from "@/utils/request";

use([
  CanvasRenderer,
  LineChart,
  AriaComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
]);

const API_PATH = "/order/stats/daily/hlb-service-transfer";
const PAGE_SIZE = 500;

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDaysAgoDate = (days) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - days);
  return formatDate(date);
};

const today = getDaysAgoDate(0);
const loading = ref(false);
const loadError = ref("");
const dailyRows = ref([]);
const quickDateValue = ref("today");
const summary = ref({ transferCount: 0 });
let requestSequence = 0;

const searchForm = reactive({
  startDate: today,
  endDate: today,
});

const quickDateOptions = [
  { label: "今天", value: "today", startOffset: 0, endOffset: 0 },
  { label: "昨天", value: "yesterday", startOffset: 1, endOffset: 1 },
  { label: "最近 2 天", value: "last_2_days", startOffset: 1, endOffset: 0 },
  { label: "最近 3 天", value: "last_3_days", startOffset: 2, endOffset: 0 },
  { label: "最近 5 天", value: "last_5_days", startOffset: 4, endOffset: 0 },
  { label: "最近 7 天", value: "last_7_days", startOffset: 6, endOffset: 0 },
  { label: "最近 30 天", value: "last_30_days", startOffset: 29, endOffset: 0 },
];

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total) => `共 ${total} 条`,
});

const formatCount = (value) => Number(value || 0).toLocaleString("zh-CN");

const normalizeRows = (rows) =>
  (rows || []).map((row) => ({
    statDate: String(row.statDate || ""),
    transferCount: Number(row.transferCount || 0),
  }));

const chartPoints = computed(() =>
  [...dailyRows.value].sort((left, right) =>
    left.statDate.localeCompare(right.statDate)
  )
);
const tableRows = computed(() => [...chartPoints.value].reverse());
const latestPoint = computed(
  () => chartPoints.value[chartPoints.value.length - 1] || null
);
const rangeLabel = computed(() =>
  searchForm.startDate === searchForm.endDate
    ? searchForm.startDate
    : `${searchForm.startDate} 至 ${searchForm.endDate}`
);
const chartAriaLabel = computed(
  () =>
    `${rangeLabel.value}合利宝转接日趋势，共${chartPoints.value.length}个有数据日期，` +
    `转接总次数${summary.value.transferCount}次。`
);

const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const chartOption = computed(() => ({
  animation: !prefersReducedMotion,
  animationDuration: 250,
  aria: {
    enabled: true,
    decal: { show: true },
    description: chartAriaLabel.value,
  },
  color: ["#1677ff"],
  tooltip: {
    trigger: "axis",
    axisPointer: { type: "line" },
    formatter: (params) =>
      `${params[0]?.axisValue || "-"}<br/>` +
      `${params[0]?.marker || ""}转接次数：${formatCount(
        params[0]?.value
      )} 次`,
  },
  legend: {
    data: ["转接次数"],
    bottom: 0,
  },
  grid: {
    top: 20,
    left: 12,
    right: 16,
    bottom: 64,
    containLabel: true,
  },
  xAxis: {
    type: "category",
    boundaryGap: false,
    data: chartPoints.value.map((row) => row.statDate),
    axisLabel: {
      hideOverlap: true,
      formatter: (value) => String(value).slice(5),
    },
    axisTick: { alignWithLabel: true },
    axisLine: { lineStyle: { color: "#bfbfbf" } },
  },
  yAxis: {
    type: "value",
    name: "次数",
    minInterval: 1,
    axisLabel: {
      formatter: (value) => Number(value).toLocaleString("zh-CN"),
    },
    splitLine: { lineStyle: { color: "#f0f0f0", type: "dashed" } },
  },
  series: [
    {
      name: "转接次数",
      type: "line",
      data: chartPoints.value.map((row) => row.transferCount),
      showSymbol: chartPoints.value.length <= 48,
      symbol: "circle",
      symbolSize: 7,
      smooth: 0.2,
      lineStyle: { width: 2.5 },
      areaStyle: { opacity: 0.08 },
    },
  ],
}));

const nowrap = { style: { whiteSpace: "nowrap" } };
const numericCell = {
  style: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
};

const columns = [
  {
    title: "日期",
    dataIndex: "statDate",
    key: "statDate",
    width: 180,
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
  {
    title: "转接次数",
    dataIndex: "transferCount",
    key: "transferCount",
    width: 180,
    align: "center",
    customCell: () => numericCell,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => `${formatCount(text)} 次`,
  },
];

const buildParams = (pageIdx, pageSize) => ({
  startDate: searchForm.startDate,
  endDate: searchForm.endDate,
  pageIdx,
  pageSize,
});

const validateSearch = () => {
  if (!searchForm.startDate || !searchForm.endDate) {
    message.warning("请选择开始日期和结束日期");
    return false;
  }
  if (searchForm.startDate > searchForm.endDate) {
    message.warning("开始日期不能晚于结束日期");
    return false;
  }
  return true;
};

const fetchData = async () => {
  if (!validateSearch()) return;

  const currentRequest = ++requestSequence;
  loading.value = true;
  loadError.value = "";
  try {
    const first = await get(analyticsUrl(API_PATH), buildParams(1, PAGE_SIZE));
    if (first.code !== 200 || !first.data) {
      throw new Error(first.content || "接口返回异常，请稍后重试");
    }

    const rows = [...(first.data.data || [])];
    const pageCount = Number(first.data.pageCount || 0);
    if (pageCount > 1) {
      const remaining = await Promise.all(
        Array.from({ length: pageCount - 1 }, (_, index) =>
          get(analyticsUrl(API_PATH), buildParams(index + 2, PAGE_SIZE))
        )
      );
      remaining.forEach((response) => {
        if (response.code !== 200 || !response.data) {
          throw new Error(response.content || "部分统计数据加载失败");
        }
        rows.push(...(response.data.data || []));
      });
    }

    if (currentRequest !== requestSequence) return;
    dailyRows.value = normalizeRows(rows);
    summary.value = {
      transferCount: Number(first.data.summary?.transferCount || 0),
    };
    pagination.current = 1;
    pagination.total = dailyRows.value.length;
  } catch (error) {
    if (currentRequest !== requestSequence) return;
    dailyRows.value = [];
    summary.value = { transferCount: 0 };
    pagination.total = 0;
    loadError.value = error?.message || "网络异常，请检查连接后重试";
    message.error("合利宝转接统计数据加载失败");
  } finally {
    if (currentRequest === requestSequence) loading.value = false;
  }
};

const handleSearch = () => fetchData();

const clearQuickDate = () => {
  quickDateValue.value = null;
};

const handleQuickDateChange = ({ target }) => {
  const option = quickDateOptions.find((item) => item.value === target.value);
  if (!option) return;
  searchForm.startDate = getDaysAgoDate(option.startOffset);
  searchForm.endDate = getDaysAgoDate(option.endOffset);
  fetchData();
};

const handleReset = () => {
  searchForm.startDate = today;
  searchForm.endDate = today;
  quickDateValue.value = "today";
  fetchData();
};

const handleTableChange = (page) => {
  pagination.current = page.current;
  pagination.pageSize = page.pageSize;
};

onMounted(fetchData);
</script>

<style scoped>
.filter-card,
.chart-card {
  margin-bottom: 16px;
}

.filter-form {
  row-gap: 8px;
}

.date-picker {
  width: 140px;
}

.quick-date-group {
  white-space: nowrap;
}

.metric-rule {
  margin-top: 8px;
}

.range-label {
  color: #595959;
}

.trend-chart {
  width: 100%;
  height: 360px;
}

.chart-summary {
  display: flex;
  align-items: flex-start;
  gap: 32px;
  width: 100%;
  margin-top: 12px;
  padding-top: 12px;
  padding-bottom: 4px;
  overflow-x: auto;
  border-top: 1px solid #f0f0f0;
  color: #595959;
  font-size: 12px;
  line-height: 1.6;
}

.summary-column {
  flex: 0 0 auto;
  min-width: max-content;
}

.summary-title {
  margin-bottom: 6px;
  color: #262626;
  font-size: 13px;
  font-weight: 600;
}

.summary-metrics {
  display: grid;
  grid-template-columns: max-content;
  gap: 2px;
}

.summary-metrics span {
  white-space: nowrap;
}

.transfer-value {
  color: #0958d9;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.chart-empty {
  display: flex;
  min-height: 360px;
  flex-direction: column;
  justify-content: center;
}

@media (max-width: 768px) {
  .filter-form {
    display: block;
  }

  .filter-form :deep(.ant-form-item) {
    margin-right: 0;
    margin-bottom: 12px;
  }

  .date-picker {
    width: 100%;
  }

  .quick-date-group {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    width: 100%;
    white-space: normal;
  }

  .quick-date-group :deep(.ant-radio-button-wrapper) {
    width: 100%;
    min-height: 44px;
    padding-inline: 8px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    line-height: 42px;
    text-align: center;
  }

  .quick-date-group :deep(.ant-radio-button-wrapper::before) {
    display: none;
  }

  .filter-form :deep(.ant-btn),
  :deep(.ant-pagination-item),
  :deep(.ant-pagination-prev),
  :deep(.ant-pagination-next) {
    min-width: 44px;
    min-height: 44px;
  }

  .trend-chart,
  .chart-empty {
    height: 320px;
    min-height: 320px;
  }

  .chart-summary {
    flex-direction: column;
    gap: 12px;
    overflow-x: visible;
  }

  .summary-column {
    min-width: 0;
  }

  .range-label {
    font-size: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hlb-transfer-stat-page * {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
  }
}
</style>
